import { Fragment, useEffect, useState } from "react";

import { FaCheck } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { TPremiumPackage, TPremiumPackageClass } from "../../../@types";
import { apis, callApi } from "../../../api";
import { useAccount } from "../../../hooks/useAccount";
import { usePremiumPackage } from "../../../hooks/usePremiumPackage";
import { useWhiteScreen } from "../../../hooks/useWhiteScreen";
import { currencyFormatter } from "../../../utils";
import { eventNames, logEvent } from "../../../utils/logEvent";
import { Container } from "../../common/Container/Container";
import Button from "../../ui/Button/Button";
import Typography, { Heading } from "../../ui/Typography/Typography";

import "./pricing.scss";

export function Pricing() {
	const { account } = useAccount();
	useWhiteScreen();
	const { premiumPackages } = usePremiumPackage();
	const navigate = useNavigate();

	type TState = {
		subscriptionId: string | null;
		confirmDialogOpen: boolean;
		loading: boolean;
	};
	const [state, setState] = useState<TState>({
		subscriptionId: null,
		confirmDialogOpen: false,
		loading: false,
	});

	useEffect(() => {
		const hash = location.hash;
		if (premiumPackages.length > 0 && hash) {
			const id = hash.slice(1);
			document.getElementById(id)?.scrollIntoView();
		}
	}, [premiumPackages]);

	const onClickSubscription =
		(
			premiumPackage: TPremiumPackage,
			premiumPackageClass: TPremiumPackageClass,
		) =>
		async () => {
			if (!account) {
				navigate(`/sign-in?redirect=/memberships#${premiumPackage.id}`);
				return;
			}
			try {
				setState((prevState) => ({ ...prevState, loading: true }));
				const response = await callApi({
					method: "POST",
					url: apis.SUBSCRIPTION,
					body: {
						priceId: premiumPackageClass.id,
					},
				});

				if (!response.paymentResult) {
					return;
				}

				logEvent(eventNames.purchasePremiumPackage, {
					package: premiumPackage.name,
					class: premiumPackageClass.id,
				});
				window.location.href = response.paymentResult.url;
			} catch (error: any) {
				if (error?.response?.data?.message) {
					toast.error(error.response.data.message);
				}
			} finally {
				setState((prevState) => ({ ...prevState, loading: false }));
			}
		};

	return (
		<Container
			className="premium-container"
			navbarOrkaProps={{ white: true }}
			loading={state.loading}>
			<div id="premium" className="section-scroll-position" />

			{premiumPackages.length > 0 &&
				premiumPackages.map((premiumPackage) => {
					return (
						<Fragment key={premiumPackage.id}>
							<div id={premiumPackage.id} className="section-scroll-position" />
							<div className="package-container content-container">
								<Heading as="h1" className="page-title">
									Membership Keys
								</Heading>
								<div className="classes-container row row--column-start">
									{premiumPackage.classes
										// .filter((classItem) => classItem.enabled)
										.map((classItem, index) => (
											<div
												key={classItem.id}
												className={`class-item${index > 0 ? " class-item--premium class-item--" + classItem.name : ""}`}>
												<Typography as="h2" className="class-item__title">
													{classItem.name}
												</Typography>
												<Typography className="class-item__price">
													{`${currencyFormatter(classItem.currency).format(
														classItem.monthlyPrice,
													)}`}
													<Typography
														as="span"
														className="class-item__frequency">
														/month
													</Typography>
												</Typography>
												{index === 0 && (
													<Typography className="feature-description">
														Features you'll love:
													</Typography>
												)}
												{index > 0 && (
													<Typography className="feature-description">
														{`All in ${premiumPackage.classes[index - 1].name}, plus:`}
													</Typography>
												)}
												<ul className="list feature-list">
													{classItem.features.map((feature) => (
														<li className="feature-container" key={feature}>
															<FaCheck className="normal-icon feature-check-icon" />
															<Typography className="feature__item">
																{feature}
															</Typography>
														</li>
													))}
												</ul>
												<div style={{ flex: 1 }} />
												{index === 1 && (
													<Button
														className="upgrade-button primary-action"
														disabled={account?.isMember}
														onClick={onClickSubscription(
															premiumPackage,
															classItem,
														)}>
														{account?.isMember
															? "You've got this!"
															: "Join Us!"}
													</Button>
												)}
											</div>
										))}
								</div>
							</div>
						</Fragment>
					);
				})}
		</Container>
	);
}
